import type { Ref } from 'react'
import { Link } from 'react-router-dom'
import type { ConcertStatus } from '../domain/time.ts'
import type { Concert } from '../domain/program-schema.ts'
import type { SharedAnnotation, Vote } from '../store/types.ts'
import { t } from '../i18n.ts'
import { DiceIcon } from './DiceControls.tsx'
import { stageColor } from './stageColor.ts'
import './ConcertRow.css'

const STATUS_LABEL: Record<ConcertStatus, string> = {
  ferdig: t.day.ended,
  paagaar: t.day.live,
  kommer: t.day.upcoming,
}

/**
 * How many friends' lines one row will carry (R33).
 *
 * Two. A friend's annotations arrive for every concert they have annotated, not only
 * the ones they picked out and sent (R12), and a rating alone is enough to earn a
 * line — so at the twenty-five-friend cap a much-loved concert could otherwise bury
 * its own artist name under twenty-five prefixed lines, on the one surface whose
 * whole job is scanning twenty-two rows on a phone. Nothing is lost by cutting here:
 * the concert page renders the full set, and the row is a link to it.
 */
const MAX_SHARED_LINES = 2

function voteLabel(vote: Vote): string {
  if (vote === 'up') return t.concert.thumbsUp
  if (vote === 'down') return t.concert.thumbsDown
  return ''
}

/**
 * A shared annotation with the name it renders under already decided.
 *
 * Resolving that name needs the friend list — R14 takes it from the address the user
 * typed when they added the person, which the annotation document has never carried —
 * so `DayView` resolves it once for the whole day and hands it down. That keeps this
 * component presentational: one subscription to the friend list for the list, rather
 * than twenty-two rows each asking who wrote what.
 */
export interface PrefixedShared extends SharedAnnotation {
  /** What to call whoever wrote it (R13, R14). Never empty — see `friendPrefix`. */
  prefix: string
}

/**
 * One concert in the day list.
 *
 * Hierarchy is fixed deliberately: the artist name is primary, start time and stage
 * are secondary, and the reader's own verdict — thumb, die, or both — sits at the
 * trailing edge. Genre chips are *not* here — they live on the detail view — so 22
 * rows stay scannable at 390px.
 *
 * Notes are the exception to that restraint. A note is something the user wrote
 * themselves, and having to open every concert to find out which one they said
 * "husk å komme tidlig" about defeats the point of the list. Own note and friends'
 * shared notes therefore render in place, clamped, so an annotated row grows by a
 * line or two and an unannotated row is exactly as it was.
 *
 * Friends' lines carry a name and, where there is one, a die (R13, R19) — and there
 * are at most two of them, with the rest named as a count (R33). That is the whole
 * restraint holding: a row is a scannable summary of what is here and who has been,
 * and the concert page is where the full set is read.
 */
export function ConcertRow({
  concert,
  status,
  vote,
  dice = null,
  note = '',
  shared = [],
  ref,
}: {
  concert: Concert
  status: ConcertStatus
  vote: Vote
  /** The reader's own terningkast, 1–6, or null when they have not rated it. */
  dice?: number | null
  /** The reader's own note, already trimmed. Empty when there is none. */
  note?: string
  /** What friends have left on this concert, each carrying the name to render it under. */
  shared?: readonly PrefixedShared[]
  /**
   * Set by `DayView` on the single row it wants to bring into view, and left off
   * the other twenty-one. React 19 takes `ref` as an ordinary prop, so this needs
   * no `forwardRef` wrapper — unlike `NowMarker`, which predates that.
   */
  ref?: Ref<HTMLLIElement>
}) {
  /*
   * What friends have to say here, in the order the row can afford to say it.
   *
   * A line survives if it has words, a thumb, or a die. A terningkast alone is a
   * verdict and not an empty record (R18), so it earns its line the same way a thumb
   * always did — otherwise a friend's rating would show on the concert page and
   * nowhere in the list. Only an annotation carrying none of the three is silent, and
   * a blank line under the artist name would read as a rendering bug.
   *
   * Then notes first. When the row has to drop someone, a sentence a friend wrote is
   * worth more of the two lines than a number is, and the number still reaches the
   * reader through the count and the concert page. `sort` is stable, so within each
   * group the store's own order stands.
   */
  const sharedLines = shared
    .map((a) => {
      const words = a.note.trim()
      return {
        key: `${a.uid}-${a.concertId}`,
        from: a.prefix,
        body: words || voteLabel(a.vote),
        dice: a.dice ?? null,
        hasWords: words !== '',
      }
    })
    .filter((line) => line.body !== '' || line.dice !== null)
    .sort((a, b) => Number(b.hasWords) - Number(a.hasWords))

  const shownShared = sharedLines.slice(0, MAX_SHARED_LINES)
  const hiddenShared = sharedLines.length - shownShared.length

  /*
   * Two questions, deliberately not one attribute.
   *
   * `data-notes` is the layout question — does this row carry lines under the artist
   * name, and is it therefore taller than its neighbours. `data-annotated` is the
   * honest one: the reader has left a mark here, and a die alone counts. Folding a
   * die into the layout flag would top-align a row that has nothing extra in it and
   * shunt its clock away from the name.
   */
  const hasNotes = note !== '' || shownShared.length > 0
  const annotated = hasNotes || dice !== null

  return (
    <li
      ref={ref}
      className="concert-row"
      data-status={status}
      data-annotated={annotated || undefined}
      data-notes={hasNotes || undefined}
    >
      <Link to={`/konsert/${concert.id}`} className="concert-row__link">
        <span className="concert-row__time">{concert.start}</span>

        <span className="concert-row__main">
          <span className="concert-row__name">{concert.name}</span>
          <span className="concert-row__meta">
            <span className="concert-row__stage">
              <span
                className="concert-row__dot"
                style={{ background: stageColor(concert.stage) }}
                aria-hidden="true"
              />
              {concert.stage}
            </span>
            <span className="concert-row__until">–{concert.end}</span>
          </span>

          {note !== '' && (
            <span className="concert-row__note">
              <span className="sr-only">{t.concert.noteLabel}: </span>
              {note}
            </span>
          )}

          {shownShared.map((line) => (
            <span className="concert-row__shared" key={line.key}>
              <span className="concert-row__shared-from">{line.from}:</span>{' '}
              {line.dice !== null && (
                <>
                  {/* Inline with the name that owns it, never in the trailing column:
                      that column is the reader's own verdict, and a friend's die
                      landing there would read as the reader's at a glance. */}
                  <span
                    className="concert-row__shared-dice"
                    title={t.concert.diceValue(line.dice)}
                  >
                    <DiceIcon value={line.dice} />
                    <span className="sr-only">{t.concert.diceValue(line.dice)}</span>
                  </span>
                  {line.body !== '' && ' '}
                </>
              )}
              {line.body}
            </span>
          ))}

          {/* Named rather than silently dropped: a row that quietly stopped at two
              would tell a reader with five friends that only two had been here. */}
          {hiddenShared > 0 && (
            <span className="concert-row__more">{t.day.moreFriends(hiddenShared)}</span>
          )}
        </span>

        <span className="concert-row__vote">
          {vote === 'up' && (
            <span className="concert-row__thumb concert-row__thumb--up" title={t.concert.thumbsUp}>
              <ThumbUpIcon />
              <span className="sr-only">{t.concert.thumbsUp}</span>
            </span>
          )}
          {vote === 'down' && (
            <span
              className="concert-row__thumb concert-row__thumb--down"
              title={t.concert.thumbsDown}
            >
              <ThumbDownIcon />
              <span className="sr-only">{t.concert.thumbsDown}</span>
            </span>
          )}
          {dice !== null && (
            <span className="concert-row__dice" title={t.concert.diceValue(dice)}>
              {/* The same face the concert page's control draws — the list and the
                  control must not disagree about what a 5 looks like. */}
              <DiceIcon value={dice} />
              <span className="sr-only">{t.concert.diceValue(dice)}</span>
            </span>
          )}
        </span>

        <span className="sr-only">{STATUS_LABEL[status]}</span>
      </Link>
    </li>
  )
}

export function ThumbUpIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <path
        d="M7 21V9.5L12.2 3a1.6 1.6 0 012.7 1.5L13.8 9H19a2 2 0 011.95 2.45l-1.6 7A2 2 0 0117.4 20H7zM7 21H4a1 1 0 01-1-1v-9a1 1 0 011-1h3"
        stroke="currentColor"
        strokeWidth="1.9"
        strokeLinejoin="round"
        strokeLinecap="round"
      />
    </svg>
  )
}

export function ThumbDownIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <path
        d="M17 3v11.5L11.8 21a1.6 1.6 0 01-2.7-1.5L10.2 15H5a2 2 0 01-1.95-2.45l1.6-7A2 2 0 016.6 4H17zM17 3h3a1 1 0 011 1v9a1 1 0 01-1 1h-3"
        stroke="currentColor"
        strokeWidth="1.9"
        strokeLinejoin="round"
        strokeLinecap="round"
      />
    </svg>
  )
}
